/**
 * SidebarPromoCard Component
 *
 * Tarjeta publicitaria moderna para el sidebar
 * Basada en tendencias UI 2025: Glassmorphism, gradientes, micro-animaciones
 *
 * @created 2025-01-08
 */

import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { SparklesIcon, RocketLaunchIcon, BoltIcon } from '@heroicons/react/24/solid';

export type PromoVariant = 'upgrade' | 'feature' | 'discount' | 'event';

interface SidebarPromoCardProps {
  variant?: PromoVariant;
  onCtaClick?: () => void;
}

const promoContent = {
  upgrade: {
    badge: '🚀 PRO',
    badgeColor: 'from-orange-500 via-orange-600 to-orange-400',
    title: 'Desbloquea todo el poder',
    subtitle: 'Automatización IA + Analytics ilimitados',
    cta: 'Próximamente',
    icon: RocketLaunchIcon,
    gradient: 'from-orange-400/20 via-orange-500/20 to-blue-400/15',
  },
  feature: {
    badge: '✨ NUEVO',
    badgeColor: 'from-orange-500 via-orange-600 to-orange-400',
    title: 'AI Insights está aquí',
    subtitle: 'Predicciones inteligentes de ventas',
    cta: 'Descubrir',
    icon: SparklesIcon,
    gradient: 'from-orange-500/20 via-orange-600/20 to-orange-400/15',
  },
  discount: {
    badge: '🔥 50% OFF',
    badgeColor: 'from-orange-600 via-orange-500 to-orange-400',
    title: 'Oferta por tiempo limitado',
    subtitle: 'Solo esta semana - Plan anual',
    cta: 'Aprovechar',
    icon: BoltIcon,
    gradient: 'from-orange-600/25 via-orange-500/20 to-orange-400/20',
  },
  event: {
    badge: '📅 EVENTO',
    badgeColor: 'from-orange-500 via-orange-600 to-blue-500',
    title: 'Webinar: Sales Automation',
    subtitle: 'Mañana 4PM - Cupos limitados',
    cta: 'Registrarme',
    icon: SparklesIcon,
    gradient: 'from-orange-500/20 via-blue-500/15 to-orange-400/15',
  },
};

export const SidebarPromoCard: React.FC<SidebarPromoCardProps> = ({
  variant = 'upgrade',
  onCtaClick,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const content = promoContent[variant];
  const Icon = content.icon;

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animación de pulso sutil en el glow
      if (glowRef.current) {
        gsap.to(glowRef.current, {
          scale: 1.05,
          opacity: 0.6,
          duration: 2,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      }

      // Animación flotante del badge
      if (badgeRef.current) {
        gsap.to(badgeRef.current, {
          y: -2,
          duration: 1.5,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      }

      // Rotación sutil del ícono
      if (iconRef.current) {
        gsap.to(iconRef.current, {
          rotate: 5,
          duration: 2,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
        });
      }
    });

    return () => ctx.revert();
  }, []);

  const handleHover = () => {
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        scale: 1.02,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  };

  const handleHoverOut = () => {
    if (cardRef.current) {
      gsap.to(cardRef.current, {
        scale: 1,
        duration: 0.3,
        ease: 'power2.out',
      });
    }
  };

  return (
    <div className="relative px-4 pb-4">
      {/* Card con glassmorphism - bordes redondeados */}
      <div
        ref={cardRef}
        onMouseEnter={handleHover}
        onMouseLeave={handleHoverOut}
        className="relative overflow-hidden rounded-2xl cursor-pointer group"
        onClick={onCtaClick}
      >
        {/* Glow effect animado - más intenso */}
        <div
          ref={glowRef}
          className={`absolute inset-0 bg-gradient-to-br ${content.gradient} opacity-60 blur-2xl`}
        />

        {/* Glassmorphism container con blur y bordes redondeados */}
        <div className="relative bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl rounded-2xl border border-orange-300/60 dark:border-blue-500/50 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div className="p-4 space-y-3 relative">
            {/* Badge flotante con gradiente diagonal */}
            <div ref={badgeRef} className="inline-block">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-br ${content.badgeColor} text-white text-xs font-bold shadow-lg`}>
                <span>{content.badge}</span>
              </div>
            </div>

            {/* Ícono con rotación */}
            <div ref={iconRef} className="inline-block">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-blue-500/20 dark:from-orange-500/30 dark:to-blue-500/20 backdrop-blur-sm flex items-center justify-center border border-orange-400/30 dark:border-orange-400/40 shadow-lg">
                <Icon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
            </div>

            {/* Título y subtítulo */}
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                {content.title}
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 leading-snug">
                {content.subtitle}
              </p>
            </div>

            {/* CTA Button con gradiente diagonal intenso */}
            <button
              className={`w-full py-2.5 px-4 rounded-xl bg-gradient-to-br ${content.badgeColor} text-white text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform group-hover:scale-105`}
              onClick={(e) => {
                e.stopPropagation();
                onCtaClick?.();
              }}
            >
              {content.cta}
              <span className="inline-block ml-1 transform group-hover:translate-x-1 transition-transform duration-300">→</span>
            </button>
          </div>

          {/* Decorative gradient overlay diagonal */}
          <div className={`absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br ${content.gradient} opacity-70 dark:opacity-50 blur-3xl pointer-events-none`} />
        </div>

        {/* Shimmer effect on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
        </div>
      </div>
    </div>
  );
};
